import { useMemo, useState } from "react";
import BaseTable from "../common/BaseTable";
import TabContainer from "../common/TabContainer";
import { useMarkdownRenderer } from "../hooks/useMarkdownRenderer";
import { useFieldDesignData } from "../hooks/useTableData";
import type { FieldDesignTableProps } from "../types";
import styles from "./index.module.scss";

const HEADER_LABELS: Record<string, string> = {
  label: "フィールド名",
  code: "フィールドコード",
  type: "タイプ",
};

const FieldDesignTable: React.FC<FieldDesignTableProps> = ({
  formData,
  content,
  mode,
  maxApps = 3,
  maxFields = 5,
  className = "",
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const {
    parsed: parsedContent,
    isMarkdown,
    isUnknownJson,
    content: rawContent,
  } = useFieldDesignData(formData, content);
  const { renderMarkdown } = useMarkdownRenderer();

  const apps = parsedContent?.apps || [];
  const displayApps = mode === "compact" ? apps.slice(0, maxApps) : apps;

  // useMemoをトップレベルに配置（Reactフックのルールに従う）
  const tabs = useMemo(() => {
    if (!displayApps.length) return [];
    return displayApps.map((app) => {
      // 各アプリごとにtableDataを計算
      const headers = ["label", "code", "type"];
      const rows: string[][] = [];
      // propertiesがundefinedの場合も考慮
      (app.properties ?? []).forEach((property) => {
        // 旧形式: property.required.rows[0][1] / [3]
        // 新形式（構造化出力）: property.フィールドコード / property.フィールドタイプ
        const prop = property as unknown as Record<string, unknown>;
        const code =
          property.required?.rows?.[0]?.[1] ?? (prop["フィールドコード"] as string) ?? "";
        const type =
          property.required?.rows?.[0]?.[3] ?? (prop["フィールドタイプ"] as string) ?? "";
        const label = property.label ?? (prop["フィールド名"] as string) ?? "";
        rows.push([label, code, type]);
      });

      return {
        key: app.app_id,
        label: app.app_name,
        content: (
          <BaseTable
            mode={mode}
            headers={headers}
            rows={rows}
            labelMap={HEADER_LABELS}
            maxRows={mode === "compact" ? maxFields : undefined}
          />
        ),
      };
    });
  }, [displayApps, mode, maxFields]);

  // JSONだが期待する形式でない場合は整形して表示
  if (isUnknownJson) {
    try {
      const formatted = JSON.stringify(JSON.parse(rawContent), null, 2);
      return (
        <div
          className={`${styles["wrapper"]} ${mode === "compact" ? styles["wrapper--compact"] : ""} ${className}`}
        >
          <pre className={styles["jsonPreview"]}>
            {mode === "compact" ? `${formatted.slice(0, 300)}...` : formatted}
          </pre>
        </div>
      );
    } catch {
      return <div className={styles["empty"]}>データがありません</div>;
    }
  }

  if (isMarkdown) {
    // 空のコンテンツの場合は「データがありません」を表示
    if (!rawContent || rawContent.trim() === "") {
      return <div className={styles["empty"]}>データがありません</div>;
    }
    // compactモードでは重いMarkdownレンダリングを避けてテキスト表示
    if (mode === "compact") {
      const truncated = rawContent.substring(0, 300);
      return (
        <div className={`${styles["wrapper"]} ${styles["wrapper--compact"]} ${className}`}>
          <div className={styles["markdownContent--compact"]}>
            <pre className={styles["markdownPreviewText"]}>{truncated}...</pre>
          </div>
        </div>
      );
    }
    return (
      <div className={`${styles["wrapper"]} ${className}`}>
        <div className={styles["markdownContent"]}>{renderMarkdown(rawContent)}</div>
      </div>
    );
  }

  if (!apps.length) {
    return <div className={styles["empty"]}>データがありません</div>;
  }

  return (
    <div
      className={`${styles["wrapper"]} ${mode === "compact" ? styles["wrapper--compact"] : ""} ${className}`}
    >
      <TabContainer
        mode={mode}
        tabs={tabs}
        selectedIndex={selectedTab}
        onSelect={setSelectedTab}
        maxTabs={mode === "compact" ? maxApps : undefined}
      />
    </div>
  );
};

export default FieldDesignTable;
